// ─────────────────────────────────────────────────────────────────────────
// Creative Strategist deliverable categories (spec §52 and §56).
//
// Shared deliberately: the Deliverables tab filters on these and the Results
// page counts by them. If the two disagreed, a user would see "20 static ads"
// on Results and find nineteen behind the Ads filter — so there is one map.
// ─────────────────────────────────────────────────────────────────────────

export const CS_DELIVERABLE_FILTERS = [
  "all",
  "strategy",
  "landing_pages",
  "ads",
  "video",
  "ugc",
  "content",
  "research",
  "templates",
] as const;

export type CsDeliverableFilter = (typeof CS_DELIVERABLE_FILTERS)[number];

export const CS_DELIVERABLE_FILTER_LABELS: Record<CsDeliverableFilter, string> = {
  all: "All",
  strategy: "Strategy",
  landing_pages: "Landing Pages",
  ads: "Ads",
  video: "Video",
  ugc: "UGC",
  content: "Content",
  research: "Research",
  templates: "Templates",
};

/** Deliverable type → the one filter it belongs under. */
const CATEGORY_BY_TYPE: Record<string, CsDeliverableFilter> = {
  static_ad: "ads",
  meme: "ads",
  landing_page: "landing_pages",
  product_page: "landing_pages",
  quiz_funnel: "landing_pages",
  advertorial: "content",
  listicle: "content",
  email: "content",
  slideshow: "content",
  document: "content",
  ugc_video: "ugc",
  ugc_script: "ugc",
  video_ad: "video",
  video: "video",
  storyboard: "video",
  strategy: "strategy",
  campaign_territory: "strategy",
  angle: "strategy",
  persona: "strategy",
  offer: "strategy",
  research: "research",
  research_report: "research",
  template: "templates",
  ad_template: "templates",
  lp_template: "templates",
};

/**
 * Which filter a deliverable sits under. Unknown types fall to Content rather
 * than disappearing: a deliverable that exists must be reachable from the
 * library, even if a later phase invents a type this map has not met.
 */
export function csDeliverableCategory(deliverableType: string): CsDeliverableFilter {
  return CATEGORY_BY_TYPE[deliverableType] ?? "content";
}

/** Human label for one of something, used in Results counts. */
const SINGULAR_LABELS: Record<string, string> = {
  static_ad: "static ad",
  meme: "meme",
  landing_page: "landing page",
  product_page: "product page",
  quiz_funnel: "quiz funnel",
  advertorial: "advertorial",
  listicle: "listicle",
  email: "email",
  slideshow: "slideshow",
  ugc_video: "UGC video",
  ugc_script: "UGC script",
  video_ad: "video ad",
  storyboard: "storyboard",
  document: "document",
  research_report: "research report",
};

export function csDeliverableTypeLabel(deliverableType: string, count = 1): string {
  const base =
    SINGULAR_LABELS[deliverableType] ?? deliverableType.replace(/_/g, " ").trim();
  if (count === 1) return base;
  return base.endsWith("s") ? base : `${base}s`;
}

/** Verdicts, in the words the user reads. */
export const CS_REVIEW_VERDICT_LABELS: Record<string, string> = {
  pass: "Passed review",
  revise: "Sent back",
  escalate: "Needs you",
  user_approved: "You approved",
  not_reviewed: "Awaiting review",
};

export const CS_REVIEW_VERDICT_CLASSES: Record<string, string> = {
  pass: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  revise: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  escalate: "bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300",
  user_approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  not_reviewed: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};
